if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      get productMediaWrapper() {
        return (this._productMediaWrapper = this._productMediaWrapper || this.querySelector('.product__media-wrapper'));
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.prepareProductDetails();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }

        if (this.productMediaWrapper) {
          this.blockPositionHandler = this.handleBlocksPosition.bind(this);
          this.handleBlocksPosition();
          document.addEventListener('matchMobile', this.blockPositionHandler);
          document.addEventListener('unmatchMobile', this.blockPositionHandler);
        }
      }

      mergeProductSpecifications() {
        const description = this.querySelector('.product__details-wrapper .product__block--description');
        if (!description) return;
        const normalise = (text) => text.trim().replace(/^:\s*|\s*:$/g, '').replace(/\s+/g, ' ').toLowerCase();
        const title = this.querySelector('.product__title');
        const firstHeading = description.firstElementChild;
        if (firstHeading && /^H[1-6]$/.test(firstHeading.tagName) && title && normalise(firstHeading.textContent) === normalise(title.textContent)) firstHeading.remove();

        // Only structured specification lists/tables are merged; other source HTML stays intact.
        const heading = [...description.querySelectorAll('h2,h3,h4,h5,h6')].find((node) => /^specifications?:?$/.test(normalise(node.textContent)));
        const list = heading?.nextElementSibling;
        if (!list || !['UL', 'OL', 'TABLE'].includes(list.tagName)) return;
        const existingTable = list.tagName === 'TABLE';
        const items = [...list.children];
        if (existingTable ? !list.rows.length || [...list.rows].some((row) => row.cells.length !== 2) : !items.length || items.some((item) => item.tagName !== 'LI' || !item.querySelector('strong'))) return;
        const table = existingTable ? list : document.createElement('table');
        table.className = 'nyi-specifications';
        const body = table.tBodies[0] || table.createTBody();
        if (!existingTable) items.forEach((item) => {
          const label = item.querySelector('strong');
          const row = body.insertRow();
          const cell = document.createElement('th');
          cell.scope = 'row';
          const labelText = label.textContent.trim();
          const separator = labelText.indexOf(':');
          cell.textContent = separator < 0 ? labelText : labelText.slice(0, separator).trim();
          const value = item.cloneNode(true);
          // Preserve a value split across bold markup, including its original text-node position.
          value.querySelector('strong').replaceWith(document.createTextNode(separator < 0 ? '' : labelText.slice(separator + 1).trimStart()));
          row.append(cell);
          row.insertCell().append(...value.childNodes);
          const first = [...row.cells[1].childNodes].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
          if (first) first.textContent = first.textContent.replace(/^\s*:\s*/, '');
        });
        const sourceTable = this.querySelector('[data-source-specifications]');
        if (sourceTable) {
          [...sourceTable.rows].forEach((row) => {
            const pair = [...row.cells].map((cell) => normalise(cell.textContent));
            const label = (text) => normalise(text).replace(/^overall (length|height)$/, '$1');
            const duplicate = [...table.rows].some((existing) => existing.cells.length === pair.length && label(existing.cells[0].textContent) === label(pair[0]) && normalise(existing.cells[1].textContent) === pair[1]);
            if (!duplicate) body.append(row.cloneNode(true));
          });
          sourceTable.closest('.nyi-product-resources').remove();
        }
        if (!existingTable) list.replaceWith(table);
      }

      prepareSizeChart(description, copy) {
        const data = this.querySelector('[data-nyi-size-chart]');
        if (!data) return;
        let chart;
        try { chart = JSON.parse(data.textContent); } catch { return; }
        if (!Array.isArray(chart.source) || !Array.isArray(chart.headers)) return;
        const normal = text => text.replace(/\s+/g, '').toLowerCase();
        if (chart.sourceOptions) {
          let options;
          try { options = JSON.parse(this.querySelector('[data-nyi-product-options]')?.textContent || 'null'); } catch { return; }
          if (!Array.isArray(options) || options.length !== chart.sourceOptions.length || !chart.sourceOptions.every((source, i) => normal(source.name) === normal(options[i].name) && source.values.length === options[i].values.length && source.values.every((v, j) => normal(v) === normal(options[i].values[j])))) return;
        }
        // Fail closed when catalogue content changes; the original table remains available.
        const original = [...copy.querySelectorAll('table')].find(table => {
          const rows = [...table.rows];
          return !table.querySelector('img, a, iframe') && rows.length === chart.source.length && rows.every((row, r) => row.cells.length === chart.source[r].length && [...row.cells].every((cell, c) => cell.rowSpan === 1 && cell.colSpan === rows[0].cells[c].colSpan && normal(cell.textContent) === normal(chart.source[r][c])));
        });
        if (chart.kind === 'specifications') {
          const rows = [...description.querySelectorAll('.nyi-description-specs tr')];
          const label = text => normal(text).replace(/^overall(length|width|height)$/, '$1');
          if (!chart.sourceSpecs.every(([name, value]) => rows.some(row => row.cells.length === 2 && label(row.cells[0].textContent) === label(name) && normal(row.cells[1].textContent) === normal(value)))) return;
          if (chart.specificationsOnly) {
            // Preserve verified corrections even when the product has no original size chart.
            rows.forEach(row => {
              const matches = ([name, value]) => row.cells.length === 2 && label(row.cells[0].textContent) === label(name) && normal(row.cells[1].textContent) === normal(value);
              if (chart.representedSpecs?.some(matches) && !chart.sourceSpecs.some(matches)) row.remove();
            });
            return;
          }
        } else if (chart.kind === 'table') {
          const matches = [...copy.querySelectorAll('table')].find(table => {
            const rows = [...table.rows];
            return rows.length === chart.sourceTable.length && rows.every((row, r) => row.cells.length === chart.sourceTable[r].length && [...row.cells].every((cell, c) => {
              const source = chart.sourceTable[r][c];
              return cell.rowSpan === source.rowspan && cell.colSpan === source.colspan && normal(cell.textContent) === normal(source.text);
            }));
          });
          if (!matches) return;
          if (chart.replaceSourceTable) matches.remove();
        } else if (chart.kind === 'gallery') {
          let images;
          try { images = JSON.parse(this.querySelector('[data-nyi-product-images]')?.textContent || 'null'); } catch { return; }
          const asset = url => { const u = new URL(url, location.href); return decodeURIComponent(u.pathname.split('/').pop()); };
          if (!Array.isArray(images) || !images.some(url => asset(url) === asset(chart.sourceImage))) return;
        } else if (chart.kind === 'image') {
          if (![...copy.querySelectorAll('img')].some(img => img.getAttribute('src') === chart.sourceImage)) return;
        } else if (!original) return;
        if (chart.sourceImageNote) {
          const source = [...copy.querySelectorAll('img')].find(img => img.getAttribute('src') === chart.sourceImage);
          if (source) {
            const note = document.createElement('p');
            note.className = 'nyi-chart-note';
            note.textContent = chart.sourceImageNote;
            source.before(note);
          }
        }
        const oldHeading = original?.previousElementSibling;
        if (oldHeading && /^H[2-6]$/.test(oldHeading.tagName) && /^(dimensions|size chart|sizing chart|measurements)\s*:?$/i.test(oldHeading.textContent.trim())) oldHeading.remove();
        const box = document.createElement('section');
        box.className = 'nyi-size-chart';
        if (chart.preserveDiagrams) box.dataset.preserveDiagrams = 'true';
        box.setAttribute('aria-label', 'Dimensions and fit');
        const heading = document.createElement('h3');
        heading.textContent = 'Dimensions & fit';
        box.append(heading);
        const table = document.createElement('table');
        table.className = 'nyi-size-chart-table';
        table.style.setProperty('--nyi-chart-columns', Math.min(3, Math.max(1, chart.metrics.length)));
        const caption = table.createCaption();
        caption.textContent = 'Product measurements by model or option';
        caption.className = 'visually-hidden';
        const head = table.createTHead().insertRow();
        chart.order.forEach(i => {
          const th = document.createElement('th');
          th.scope = 'col';
          th.textContent = chart.headers[i];
          head.append(th);
        });
        const body = table.createTBody();
        chart.source.slice(1).forEach(values => {
          const row = body.insertRow();
          chart.order.forEach(i => {
            const cell = document.createElement(i === chart.identity[0] ? 'th' : 'td');
            if (cell.tagName === 'TH') cell.scope = 'row';
            const kind = chart.identity.includes(i) ? 'identity' : chart.fit.includes(i) ? 'fit' : chart.metrics.includes(i) ? 'metric' : 'other';
            cell.className = 'nyi-chart-' + kind;
            const label = document.createElement('span');
            label.className = 'nyi-chart-label';
            label.setAttribute('aria-hidden', 'true');
            const unit = chart.headers[i].match(/\s*\((mm|cm)\)\s*$/i);
            label.textContent = unit ? chart.headers[i].slice(0, unit.index) : chart.headers[i];
            const value = document.createElement('span');
            value.className = 'nyi-chart-value';
            value.textContent = values[i] || '—';
            cell.append(label, value);
            if (unit && values[i] && !/[a-z]/i.test(values[i])) {
              const suffix = document.createElement('span');
              suffix.className = 'nyi-chart-unit';
              suffix.setAttribute('aria-hidden', 'true');
              suffix.textContent = ' ' + unit[1];
              cell.append(suffix);
            }
            row.append(cell);
          });
        });
        box.append(table);
        if (chart.note) {
          const note = document.createElement('p');
          note.className = 'nyi-chart-note';
          note.textContent = chart.note;
          box.append(note);
        }
        if (chart.fit.some(i => /cabinet/i.test(chart.headers[i]))) {
          const note = document.createElement('p');
          note.className = 'nyi-chart-note';
          note.textContent = 'Cabinet fit and product dimensions are different measurements. Check both before choosing.';
          box.append(note);
        }
        const specs = description.querySelector(':scope > .nyi-description-specs');
        if (specs) specs.before(box); else description.append(box);
        original?.remove();
        // Put the buying measurements ahead of lengthy source copy, without rewriting or hiding it.
        const title = this.querySelector('.product__title')?.textContent.trim();
        if (title) {
          const intro = document.createElement('p');
          intro.className = 'nyi-size-intro';
          intro.textContent = title;
          description.prepend(intro);
        }
        description.classList.add('nyi-description-grid');
        description.append(copy);
        // Remove only repeated fit/colour facts whose values are represented in the verified chart.
        const key = text => text.toLowerCase().replace(/\((?:mm|cm|t)\)/g, '').replace(/[^a-z]/g, '').replace('color', 'colour');
        specs?.querySelectorAll('tr').forEach(row => {
          if (row.cells.length !== 2) return;
          if (chart.representedSpecs?.some(([name, value]) => normal(name) === normal(row.cells[0].textContent) && normal(value) === normal(row.cells[1].textContent))) { row.remove(); return; }
          const label = key(row.cells[0].textContent);
          if (!/^(length|width|height|depth|cabinet.*|internalcabinet.*|colour)$/.test(label)) return;
          const index = chart.source[0].findIndex(h => key(h) === label);
          if (index < 0) return;
          const unit = chart.source[0][index].match(/\((mm|cm)\)/i)?.[1] || '';
          const values = chart.source.slice(1).map(r => normal(r[index] + (unit && !/[a-z]/i.test(r[index]) ? unit : '')));
          const parts = row.cells[1].textContent.split(',').map(normal);
          if (parts.length && parts.every(value => value && values.includes(value))) row.remove();
        });
      }

      prepareProductDetails() {
        let description = this.querySelector('.product__details-wrapper .product__block--description');
        const standaloneSpecs = this.querySelector('.product__details-wrapper [data-source-specifications]');
        if (!description && standaloneSpecs) {
          description = document.createElement('div');
          description.className = 'product__block product__block--description rte text-subtext';
          standaloneSpecs.closest('.nyi-product-resources').before(description);
        }
        if (!description || description.dataset.organised) return;
        this.mergeProductSpecifications();
        description.dataset.organised = 'true';
        const copy = document.createElement('div');
        copy.className = 'nyi-description-copy';
        copy.append(...description.childNodes);
        description.append(copy);

        // Collapse background explanations only; features, instructions and safety stay visible.
        [...copy.children].filter(el => /^H[2-6]$/.test(el.tagName) && /^why\b/i.test(el.textContent.trim())).forEach(heading => {
          const nodes = [];
          for (let node = heading.nextElementSibling; node && node.tagName === 'P' && !node.querySelector('img,iframe'); node = node.nextElementSibling) nodes.push(node);
          if (nodes.reduce((n, node) => n + node.textContent.length, 0) < 600) return;
          // Safety and fitting instructions must remain visible, even under a background heading.
          if (/safety|warning|caution|hazard|flammab|ventilat|protective (?:gloves|equipment|clothing|eyewear)|keep (?:away|out)|avoid contact|do not|must not|clearance|installation|compatible|incompatible/i.test(heading.textContent + nodes.map(node => node.textContent).join(' '))) return;
          const details = document.createElement('details');
          details.className = 'nyi-description-more';
          const summary = document.createElement('summary');
          summary.textContent = 'More product information';
          heading.replaceWith(details);
          details.append(summary, heading, ...nodes);
        });

        const table = copy.querySelector('table.nyi-specifications');
        const sourceTable = this.querySelector('[data-source-specifications]');
        if (table || sourceTable) {
          const specs = document.createElement('div');
          specs.className = 'nyi-description-specs';
          const heading = document.createElement('h3');
          heading.textContent = 'Specifications';
          specs.append(heading);
          if (table) {
            const oldHeading = table.previousElementSibling;
            if (oldHeading && /^H[2-6]$/.test(oldHeading.tagName) && /^specifications?:?$/i.test(oldHeading.textContent.trim())) oldHeading.remove();
            specs.append(table);
          }
          if (sourceTable) {
            const sourceBlock = sourceTable.closest('.nyi-product-resources');
            specs.append(sourceTable);
            sourceBlock?.remove();
          }
          description.append(specs);
          description.classList.add('nyi-description-grid');
        }
        this.prepareSizeChart(description, copy);
        description.append(...copy.querySelectorAll(':scope > .nyi-description-more'));
        const firstList = [...copy.children].find(el => el.tagName === 'UL');
        if (firstList && firstList.previousElementSibling?.tagName === 'P') {
          const heading = document.createElement('h3');
          heading.textContent = 'At a glance';
          firstList.before(heading);
        }
          copy.querySelectorAll('table').forEach(table => {
            const scroll = document.createElement('div');
            scroll.className = 'nyi-description-table-scroll';
            scroll.tabIndex = 0;
            scroll.setAttribute('role', 'region');
            scroll.setAttribute('aria-label', 'Product data table; scroll horizontally if needed');
            table.before(scroll);
            scroll.append(table);
          });
        this.finishProductDetails(description, copy);
      }

      finishProductDetails(description, copy) {
        // Keep source copy intact in a disclosure; show critical instructions outside it.
        const title = this.querySelector('.product__title')?.textContent.trim();
        const critical = /\bsafety\b|\bwarning\b|\bcaution\b|hazard|flammab|keep (?:away|out)|avoid contact|do not|must not|clearance|installation|incompatible|minimum (?:opening|cabinet)|wear protective|dimensions|size chart/i;
        description.querySelectorAll('.nyi-size-intro').forEach(el => el.remove());
        const first = [...copy.children].find(el => el.tagName === 'P' && el.textContent.trim() && !el.querySelector('img,iframe,video,table'));
        let intro = null;
        if (first && first.textContent.trim().length <= 280 && !critical.test(first.textContent)) {
          intro = first;
        } else if (title) {
          intro = document.createElement('p');
          const sentence = first?.textContent.trim().match(/^(.{35,260}?[.!?])(?:\s+[A-Z]|$)/)?.[1];
          intro.textContent = sentence && !critical.test(sentence) ? sentence : title;
        }
        if (intro) { intro.classList.add('nyi-product-intro'); description.prepend(intro); }
        // Flatten the earlier background-only disclosures before grouping supporting content.
        description.querySelectorAll('.nyi-description-more').forEach(el => {
          const nodes = [...el.childNodes].filter(n => n.nodeName !== 'SUMMARY');
          if (copy.contains(el)) el.replaceWith(...nodes); else { copy.append(...nodes); el.remove(); }
        });
        const visible = document.createElement('div');
        visible.className = 'nyi-description-critical';
        let criticalSection = false;
        [...copy.children].forEach(el => {
          if (/^H[2-6]$/.test(el.tagName)) criticalSection = critical.test(el.textContent);
          const unsummarisedDiagram = (!description.querySelector('.nyi-size-chart') || description.querySelector('.nyi-size-chart[data-preserve-diagrams]')) && (el.matches('img,table') || el.querySelector('img,table'));
          if (criticalSection || critical.test(el.textContent) || unsummarisedDiagram) visible.append(el);
        });
        if (visible.children.length) description.append(visible);
        if (copy.textContent.trim() || copy.querySelector('img,iframe,video')) {
          const details = document.createElement('details');
          details.className = 'nyi-description-more';
          const summary = document.createElement('summary');
          summary.textContent = 'More product information';
          details.append(summary, copy);
          description.append(details);
        } else copy.remove();
        description.classList.add('nyi-description-grid');
      }

      handleBlocksPosition() {
        const isMobile = FoxTheme.config.mqlMobile;
        const blocks = this.querySelectorAll(`.product__block[data-show-below-media="true"]`);

        blocks.forEach((block) => {
          const blockId = block.dataset.blockId;
          const blockPlacement = this.productMediaWrapper.querySelector(`[data-block-id="${blockId}"]`);
          if (!blockPlacement) return;

          if (isMobile) {
            const isMoveToMedia = block.hasAttribute('data-moved-below-media');
            if (isMoveToMedia) {
              block.innerHTML = blockPlacement.innerHTML;
              blockPlacement.innerHTML = '';
              block.removeAttribute('data-moved-below-media');
            }
          } else {
            blockPlacement.innerHTML = block.innerHTML;
            block.innerHTML = '';
            block.setAttribute('data-moved-below-media', '');
          }
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('BuyButtons');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          const enquiry = html.querySelector(`[data-nyi-enquiry="${this.productId}"]`);
          if (enquiry) {
            document.querySelectorAll(`[data-nyi-enquiry="${this.productId}"]`).forEach((link) => {
              link.setAttribute('href', enquiry.getAttribute('href'));
            });
          }

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks,
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut,
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            }),
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;
        document.querySelectorAll('[data-buy-now]').forEach((button) => {
          if (button.form === productForm) button.toggleAttribute('disabled', disable);
        });

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    },
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    },
  );
}
